import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { resolveUrl } from "./PhotoDropzone";

/**
 * Elegant full-screen gallery with thumbnails + keyboard nav.
 * Props:
 *   cover: string (main image URL)
 *   photos: string[] (additional photo URLs)
 *   name: string (house name, for alt text)
 */
const PhotoGallery = ({ cover, photos = [], name = "" }) => {
  const all = [cover, ...photos.filter((p) => p && p !== cover)].filter(Boolean);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % all.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + all.length) % all.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, all.length]);

  const openAt = (i) => {
    setIndex(i);
    setOpen(true);
  };

  return (
    <>
      {/* Preview grid: main + up to 4 thumbnails */}
      <div className="grid grid-cols-4 grid-rows-2 gap-2 aspect-[4/5]" data-testid="photo-gallery-preview">
        <button
          type="button"
          onClick={() => openAt(0)}
          className="col-span-4 md:col-span-3 row-span-2 relative overflow-hidden border border-[#0A1128]/10 group"
          data-testid="photo-gallery-cover"
        >
          <img
            src={resolveUrl(cover)}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.03]"
          />
          {all.length > 1 && (
            <span className="absolute bottom-3 right-3 bg-[#0A1128]/80 text-[#F4F1EB] text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 flex items-center gap-2">
              <Images size={12} strokeWidth={1.4} /> {all.length} fotos
            </span>
          )}
        </button>
        {all.slice(1, 5).map((p, i) => (
          <button
            key={p + i}
            type="button"
            onClick={() => openAt(i + 1)}
            className="hidden md:block col-span-1 relative overflow-hidden border border-[#0A1128]/10 group"
            data-testid={`photo-gallery-thumb-${i}`}
          >
            <img
              src={resolveUrl(p)}
              alt={`${name} ${i + 2}`}
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
            />
            {i === 3 && all.length > 5 && (
              <div className="absolute inset-0 bg-[#0A1128]/65 flex items-center justify-center text-[#F4F1EB] text-sm font-serif-display tracking-wide">
                +{all.length - 5}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#0A1128]/95 backdrop-blur-sm flex flex-col"
          data-testid="photo-gallery-lightbox"
        >
          <div className="flex items-center justify-between px-6 md:px-10 py-4 text-[#F4F1EB]">
            <div className="font-serif-display text-xl">{name}</div>
            <div className="flex items-center gap-5">
              <span className="text-xs tracking-[0.22em] uppercase text-[#F4F1EB]/60">
                {index + 1} / {all.length}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[#F4F1EB] hover:text-[#C5A059] transition-colors"
                data-testid="photo-gallery-close"
              >
                <X size={22} strokeWidth={1.4} />
              </button>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center px-4 md:px-16 relative">
            <button
              type="button"
              onClick={() => setIndex((i) => (i - 1 + all.length) % all.length)}
              className="absolute left-4 md:left-6 text-[#F4F1EB] hover:text-[#C5A059] transition-colors"
              data-testid="photo-gallery-prev"
              aria-label="Anterior"
            >
              <ChevronLeft size={32} strokeWidth={1.2} />
            </button>
            <img
              src={resolveUrl(all[index])}
              alt={`${name} ${index + 1}`}
              className="max-h-[78vh] max-w-full object-contain"
            />
            <button
              type="button"
              onClick={() => setIndex((i) => (i + 1) % all.length)}
              className="absolute right-4 md:right-6 text-[#F4F1EB] hover:text-[#C5A059] transition-colors"
              data-testid="photo-gallery-next"
              aria-label="Seguinte"
            >
              <ChevronRight size={32} strokeWidth={1.2} />
            </button>
          </div>

          <div className="px-6 md:px-10 py-5 overflow-x-auto flex gap-2 border-t border-[#F4F1EB]/10">
            {all.map((p, i) => (
              <button
                key={p + i}
                type="button"
                onClick={() => setIndex(i)}
                className={`shrink-0 w-20 h-14 md:w-24 md:h-16 overflow-hidden border ${i === index ? "border-[#C5A059]" : "border-[#F4F1EB]/20 opacity-60 hover:opacity-100"} transition-all`}
                data-testid={`photo-gallery-thumbstrip-${i}`}
              >
                <img src={resolveUrl(p)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoGallery;
